/* global FB */

import { ProductDrawing } from './ProductDrawing.js';
//import { PixiDrawingEngine } from './Pixi/PixiDrawingEngine.js';
import { DesignerService, ProductService } from './DesignerService.js';
import { ProductViewModelFactories } from './ProductViewModelFactories.js';
import { ProductPreviewer } from './ProductPreviewer.js';
import { ProductMaterialsLoadMode } from './ProductDrawingViewModel.js';

/**
 * This is the primary Javascript ViewModel for product pages. The ProductPageViewModel
 * is a composition of itself, a ProductEditor, a ProductPreviewer, and a PopupEditor.
 *
 * - The ProductEditor (this.productEditor) is responsible for viewing and editing product data on
 *   the page itself.
 *
 * - The ProductPreviewer (this.previewer) is responsible for displaying a preview of the product,
 *   a real-time-updated drawing of the product, and for displaying images of related products.
 *
 * - The PopupEditor (this.popupEditor) is responsible for editing additional product data in a
 *   popup view. Since the popupEditor is not needed until the user clicks a button, all related
 *   setup is done just in time (either delayed until a timer expires, or until the user clicks
 *   the button).
 *
 * This view model is also the owner of a few other composite objects:
 *
 * - A ProductDrawing (this.previewDrawing): for displaying a real-time-updated preview of the product.
 *   Conceptually the drawing belongs to the previewer (since it's a child element of the preview
 *   part of the UI), but it's owned by ProductPageViewModel and passed to the previewer by reference.
 * - Another ProductDrawing (this.editDrawing): for displaying a drawing of the product inside the
 *   popupEditor. Is owned by ProductPageViewModel and passed to the popupEditor by reference.
 *
 *   Finally, the ProductPageViewModel owns two service objects:
 *
 * - ProductService (this.productService): communicates product information with the back end service.
 * - DesignerService (this.designerService): communicates with the Langlo Designer back end service.
 */
export class ProductPageViewModel {
  constructor(config, productEditorConfig) {
    /* config is for this ViewModel itself. productEditorConfig is for a product type specific
     * configuration. */
    this.config = config;
    this.productEditorConfig = productEditorConfig;

    this.constructServices(config); // Services
    this.constructProductPreviewer(); // ProductPreviewer and PreviewDrawing
    if (!config.isProductEndOfLife && productEditorConfig.productType) {
      this.constructProductEditor(); // ProductEditor and PopupEditor
    }
  }

  /** Helper method for the class constructor. */
  constructServices() {
    this.productService = new ProductService(this.config.productServiceUrl);
    this.designerService = new DesignerService(this.config.designerServiceUrl);
    this.designerService.sessionId = this.config.sessionId;
  }

  /** Helper method for the class constructor. */
  constructProductPreviewer() {
    this.previewDrawing = this.createProductDrawing('Preview Engine', this.config.UI.productPreviewCanvas);
    // ViewModel for the UI around the preview canvas, including the tabs and carousel for related images, etc.
    this.previewer = new ProductPreviewer(this.previewDrawing, 'PreviewerVM', this.config);
    if (this.previewDrawing) {
      this.previewer.productMaterialsLoadMode = ProductMaterialsLoadMode.Delayed;
    } else {
      this.previewer.productMaterialsLoadMode = ProductMaterialsLoadMode.Never;
    }
    this.previewer.onShowcaseTabShown = (e) => this.saveActiveTabToLocalStorage();
    this.previewer.onGetIsDefaultProduct = (e) => this.productEditor.isDefaultProduct;

    this.previewer.onPreviewCarouselInitialized = () => {
      if (!this.productEditor) {
        // Maybe the product is end-of-life, in which case we can't display it
      } else if (this.productEditor.isProductRestorePending) {
        /* We should not display the product yet because it'll be displayed in the
          * productEditor.onProductChanged event handler above. */
        console.log('(callback: requestAnimationFrame) ProductPageViewModel.constructor -> SKIP displayEditedProduct() (product restore is pending).');
      } else if (this.isStarted) {
        console.group('(callback: requestAnimationFrame) ProductPageViewModel.constructor -> displayEditedProduct()...');
        this.displayEditedProduct();
        console.groupEnd();
      } else {
        /* If this view model has not already been started, then let the start() method make the
          * needed calls to display the edited product. */
        console.log('(callback: requestAnimationFrame) ProductPageViewModel.constructor -> SKIP displayEditedProduct() (isStarted = false).');
      }
    };
    this.previewer.setupPreviewCarousel();
  }

  /** Helper method for the class constructor. */
  constructProductEditor() {
    const editorConfig = this.productEditorConfig;
    const factory = ProductViewModelFactories.getFactory(editorConfig.productType);
    /* The createProductEditor method might--if session storage contains a product--communicate with
     * the remote designerService to asynchronously load the product. The async load happens on the
     * last line of the ProductEditor class constructor. */
    this.productEditor = /*async*/ factory.createProductEditor(editorConfig, this.designerService, this.productService);
    this.productEditor.onProductChanged = (editor, isUserDriven) => {
      console.group('[event: productEditor.onProductChanged] ProductPageViewModel.constructor: displayEditedProduct()...');
      this.displayEditedProduct(isUserDriven);
      console.groupEnd();
    };
    /* For SEO performance reasons we only create the Popup Editor after a delay, or when is actually
     * needed (see inside popupEditorDisplayed event handler below). */
    const popupEditorTimeoutHandle = setTimeout(() => this.createPopupEditor(), 3000);
    this.productEditor.onNeedPopupEditor = () => {
      if (!this.popupEditor) {
        // Clear the timeout and create the popupEditor now!
        clearTimeout(popupEditorTimeoutHandle);
        this.createPopupEditor();
      }
    }
    this.productEditor.popupEditorDisplayed = (prodEditor, popupEditorConfig, componentType) => {
      this.popupEditor.setComponentType(componentType);
      if (!this.popupEditor.isStarted) {
        this.popupEditor.start(prodEditor.product, popupEditorConfig);
      } else {
        this.popupEditor.displayProduct(prodEditor.product, popupEditorConfig);
      }
      this.editDrawing.engine.startRendering();
      this.hideChatButton();
      this.wasRenderingBeforePopup = this.previewDrawing.engine.stopRendering();
    };
    this.productEditor.popupEditorClosed = async (prodEditor, componentType, isOk, changedElement) => {
      if (isOk) {
        this.previewer.activatePreviewTab(); // Ensure the preview drawing tab is displayed:
        await prodEditor.updateProductFromPopupEditor(this.editDrawing.getModifiedApiProduct(), componentType, changedElement);
      }
      this.popupEditor.hideCurrentProduct();
      this.showChatButton();
      this.editDrawing.engine.stopRendering();
      if (this.wasRenderingBeforePopup) {
        this.previewDrawing.engine.startRendering();
      }
    };
    this.productEditor.onPreviewDrawingFinishLabelsToggled = (on) => {
      this.previewDrawing.toggleFinishLabels(on);
      // Ensure the most appropriate preview is displayed:
      this.previewer.displayPreviewImage(/*forceDrawing:*/ on);
    };
  }

  createProductDrawing(engineName, canvasId, otherDrawingEngine) {
    const canvas = document.getElementById(canvasId);
    if (canvas) {
      const engineOptions = this.config.drawingEngineClass.createDefaultOptions(canvas);
      engineOptions.config = this.config.engineConfig;
      if (otherDrawingEngine && this.config.drawingEngineClass.canShareLoader()) {
        engineOptions.sharedLoader = otherDrawingEngine.loader;
      }
      const drawingEngine = new this.config.drawingEngineClass(engineName, engineOptions, false);
      return new ProductDrawing(drawingEngine, this.config.finishImagesBaseUrl, this.productEditorConfig.allFinishesMap);
    }
  }

  /** ViewModel for the wizard/step-by-step popup UI for editing the product finishes, etc.. */
  createPopupEditor() {
    if (!this.popupEditor && this.previewDrawing) {
      // If the popup is wrapped in a <template> element, then clone it and append it to the DOM now:
      if (this.productEditorConfig.UI.El.popupEditorTemplate) {
        const template = document.getElementById(this.productEditorConfig.UI.El.popupEditorTemplate);
        if (template) {
          const clone = template.content.firstElementChild.cloneNode(true);
          document.body.appendChild(clone);
        }
      }
      if (typeof this.config.getPhotoSwiper === 'function') {
        const swiper = this.config.getPhotoSwiper();
        swiper.refresh();
      }

      // The editDrawing is used while editing the product (the popup editor):
      this.editDrawing = this.createProductDrawing('Edit Engine', this.config.UI.editCanvas, this.previewDrawing.engine);

      const UI = this.productEditor.config.UI;
      this.productEditor.popupEditorConfig = UI.popupEditor = UI.createPopupEditorConfig();
      this.productEditor.addPopupEditorHandlers();
      this.popupEditor = this.productEditor.createPopupEditor(this.editDrawing);
      this.popupEditor.productMaterialsLoadMode = ProductMaterialsLoadMode.AsyncNow;
    }
  }

  /**
   * Removes the "disabled" attribute from the form controls we made a change to in the disableForm method.
   */
  static enableForm(disabledControls) {
    for (i = 0; i < disabledControls.length; i++) {
      disabledControls[i].removeAttribute('disabled');
    }
  }

  /**
   * Adds the "disabled" attribute to all non-hidden and not already disabled form controls, and
   * returns an array of which ones we actually made a change to.
   */
  static disableForm(formId) {
    const disabledControls = [];
    const form = document.getElementById(formId);
    if (form) {
      const controls = form.elements;
      for (i = 0; i < controls.length; i++) {
        const control = controls[i];
        const isHidden = control.getAttribute('type') == 'hidden';
        const isDisabled = control.hasAttribute('disabled');
        if (!isDisabled && !isHidden) {
          control.setAttribute('disabled', '');
          disabledControls.push(control);
        }
      }
    }
    return disabledControls;
  }

  displayEditedProduct(isUserDriven) {
    if (this.previewer) {
      this.previewer.displayEditedProduct(this.productEditor.product, isUserDriven);
      if (isUserDriven) {
        /* If the reason for the display call was driven by the user, then also show the scroll-to-drawing button. */
        this.toggleScrollToDrawingBtn(true);
      }
    }
  }

  hideChatButton() {
    //console.log('ProductPageViewModel: hideChatButton...');
    if (typeof FB != 'undefined' && FB != null && FB.CustomerChat) {
      //console.log('ProductPageViewModel: Hiding FB customer chat...');
      FB.CustomerChat.hide();
    }
  }

  showChatButton() {
    //console.log('ProductPageViewModel: showChatButton...');
    if (typeof FB != 'undefined' && FB != null && FB.CustomerChat) {
      //console.log('ProductPageViewModel: Showing FB customer chat (with dialog)...');
      const shouldShowDialog = false;
      FB.CustomerChat.show(shouldShowDialog);
    }
  }

  restoreActiveTabFromLocalStorage() {
    const activeShowcaseTab = sessionStorage.getItem('activeShowcaseTab');
    if (activeShowcaseTab && activeShowcaseTab !== '') {
      this.previewer.activateShowcaseTab(activeShowcaseTab);
    }
  }

  saveActiveTabToLocalStorage() {
    const tabName = this.previewer.getActiveShowcaseTabName();
    if (tabName) {
      sessionStorage.setItem('activeShowcaseTab', tabName);
    }
  }

  scrollToDrawingTimerActive() {
    return this.scrollToDrawingTimerId != null;
  }

  clearScrollToDrawingTimer() {
    clearTimeout(this.scrollToDrawingTimerId);
    this.scrollToDrawingTimerId = null;
  }

  hideScrollToDrawingBtn() {
    this.clearScrollToDrawingTimer();
    this.$scrollToDrawingBtn.removeClass('visible');
  }

  /**
   * Shows or hides the button for scrolling the page up to make the preview drawing visible.
   */
  toggleScrollToDrawingBtn(on) {
    if (!on) {
      this.hideScrollToDrawingBtn();
    } else {
      // 140 is an offset, chosen by visual inspection:
      const drawingTop = this.$drawingScrollTarget.offset().top + 140;
      const windowTop = this.$window.scrollTop();
      // Only display the button if user has scrolled the page such that the drawing at most is only partially visible:
      if (windowTop > drawingTop) {
        // Show the scroll-to-drawing button:
        this.$scrollToDrawingBtn.addClass('visible');
        // Only keep the button visible x ms:
        this.scrollToDrawingTimerId = setTimeout(() => {
          this.hideScrollToDrawingBtn();
        }, 10000);
        // Hide the other scroll-to-window-top button:
        this.$scrollToTopBtn.removeClass('visible');
      } else {
        // Drawing is sufficiently visible, hide button:
        this.hideScrollToDrawingBtn();
      }
    }
  }

  /**
   * Adds scroll and click event handlers for displaying the scroll-to-drawing button (after
   * changes have caused the drawing to be updated).
   * */
  setupScrollToDrawingButton() {
    this.$drawingScrollTarget = $('.drawing-scroll-target');
    this.$scrollToDrawingBtn = $('.scroll-btn.to-drawing');
    this.$window = $(window);
    this.$scrollToDrawingBtn = $('.scroll-btn.to-drawing');
    this.$scrollToTopBtn = $('.scroll-btn.to-top');
    if (this.$scrollToDrawingBtn.length > 0) {
      this.$window.on('scroll', () => {
        // Only display scroll button until the user manually starts scrolling
        this.hideScrollToDrawingBtn();
      });
      this.$scrollToDrawingBtn.on('click', (e) => {
        e.preventDefault();
        this.$drawingScrollTarget.velocity('scroll', {
          offset: 0,
          duration: 1200,
          easing: 'easeOutExpo',
          mobileHA: false,
          complete: elements => {
          },
        });
      });
    }
  }

  // This method is called from the main view file (cshtml)
  async start() {
    this.isStarting = true;
    console.group('ProductPageViewModel.start: isStarting = true');
    try {
      if (this.productEditor) {
        this.productEditor.display();
      }
      this.restoreActiveTabFromLocalStorage();

      if (this.previewer && this.productEditor && this.productEditor.product) {
        console.group('(await) ProductPageViewModel.start: previewer.start()...');
        await this.previewer.start(this.productEditor.product, this.productEditor.isProductRestorePending);
        console.groupEnd();
      }
      if (this.previewDrawing) {
        // Enable the next line if we want the drawing engine to start its rendering loop:
        this.previewDrawing.engine.startRendering();
      }
      this.setupScrollToDrawingButton();
      this.isStarted = true;
      console.log('ProductPageViewModel.start: isStarted = true');
    } finally {
      this.isStarting = false;
      console.groupEnd();
    }
  }
}
